import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef, ReactNode } from 'react'
import s from './Banner.module.css'

/**
 * Cartouche explicatif — le bloc à fond et cadre que le prototype pose en tête d'un écran ou
 * d'un panneau : « Ajoutez ou retirez des règles : elles alimentent directement la grille… »,
 * « Associer 2 agents crée un groupe dans Équipes… ». Huit occurrences en bleu et quatre en
 * gris dans le prototype back-office — c'est un motif, pas une exception d'écran.
 *
 * ⚠️ **Ce n'est pas `InfoNote`.** Celle-ci est une glose posée sous un contrôle, sans fond ;
 * celui-ci a un fond, un cadre et une place en tête de contenu — donc une chance d'être lu.
 * Le second se reconnaît à ce qu'il explique **l'écran**, pas un champ.
 *
 * ⚠️ **L'icône est une prop, pas un enfant**, et c'est le piège qu'`InfoNote` a déjà payé : le
 * cartouche est une boîte flex, donc *chaque* enfant en devient un élément séparé par le `gap` —
 * un `<b>` au milieu d'une phrase se retrouverait détaché de ses voisins, la ponctuation
 * flottant après un blanc. Le composant range donc l'icône d'un côté et **tout le texte dans un
 * seul élément** de l'autre.
 *
 * ⚠️ **Aucun ton d'alerte.** Rouge et ambre ne sont pas dans ce motif : un cartouche explique,
 * il ne signale pas un échec — ce qui échoue se dit là où le geste a été tenté.
 */
const banner = cva(s.root, {
  variants: {
    tone: {
      info: null,
      neutral: s.toneNeutral,
    },
  },
  defaultVariants: { tone: 'info' },
})

export interface BannerProps
  extends Omit<ComponentPropsWithRef<'div'>, 'children'>, VariantProps<typeof banner> {
  /** Décorative : le texte se suffit. Posée `aria-hidden` par le composant. */
  icon?: ReactNode
  children?: ReactNode
}

export type BannerTone = NonNullable<VariantProps<typeof banner>['tone']>

export function Banner({ tone, icon, children, className, ...props }: BannerProps) {
  return (
    <div className={banner({ tone, className })} {...props}>
      {icon !== undefined && (
        <span className={s.icon} aria-hidden>
          {icon}
        </span>
      )}
      <span className={s.text}>{children}</span>
    </div>
  )
}
